import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cx } from '../../lib/cx';

export interface PopoverProps {
  /** Receives the props to spread on the trigger button (aria + onClick). */
  trigger: (props: {
    onClick: () => void;
    'aria-expanded': boolean;
    'aria-controls': string;
    'aria-haspopup': 'dialog' | 'menu';
  }) => ReactNode;
  /** Render function so the panel can close itself (e.g. after picking a menu item). */
  children: (close: () => void) => ReactNode;
  role?: 'dialog' | 'menu';
  /** Which side of the trigger the panel opens toward. */
  placement?: 'bottom-end' | 'top-start';
  className?: string;
}

/** Space kept between the panel and the edge of the window, and between the panel and its trigger. */
const EDGE = 8;
const GAP = 8;

/**
 * Click-to-open floating panel. Closes on outside click and Esc, and returns focus to the trigger.
 *
 * The panel is drawn on the page itself (a portal, fixed to the window) rather than inside the element that holds the
 * trigger, so a table or card with `overflow: hidden` can never cut a menu off. It follows the trigger on scroll and
 * resize, stays inside the window, and opens upward when there is no room below.
 */
export function Popover({ trigger, children, role = 'dialog', placement = 'bottom-end', className }: PopoverProps) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const wrapper = useRef<HTMLDivElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const panelId = useId();

  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }
    const place = () => {
      const anchor = wrapper.current?.getBoundingClientRect();
      const box = panel.current;
      if (!anchor || !box) return;
      const width = box.offsetWidth;
      const height = box.offsetHeight;
      const wantsLeft = placement === 'top-start' ? anchor.left : anchor.right - width;
      const left = Math.min(Math.max(EDGE, wantsLeft), window.innerWidth - width - EDGE);
      const below = anchor.bottom + GAP;
      const above = anchor.top - GAP - height;
      const fitsBelow = below + height <= window.innerHeight - EDGE;
      const top = placement === 'top-start' || !fitsBelow ? Math.max(EDGE, above) : below;
      setPosition({ top, left });
    };
    place();
    window.addEventListener('resize', place);
    // Capture: a scrolling table or card (not just the window) moves the trigger too.
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, placement]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent) => {
      const target = e.target as Node;
      if (!wrapper.current?.contains(target) && !panel.current?.contains(target)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        wrapper.current?.querySelector<HTMLElement>('[aria-haspopup]')?.focus();
      }
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={wrapper} className="relative">
      {trigger({
        onClick: () => setOpen((o) => !o),
        'aria-expanded': open,
        'aria-controls': panelId,
        'aria-haspopup': role,
      })}
      {open
        ? createPortal(
            <div
              ref={panel}
              id={panelId}
              role={role}
              style={position ? { top: position.top, left: position.left } : { top: 0, left: 0, visibility: 'hidden' }}
              className={cx(
                'fixed z-(--mbm-z-popover) rounded-(--mbm-radius-md) border border-border-line bg-surface-elevated shadow-(--mbm-shadow-lg)',
                className,
              )}
            >
              {children(() => setOpen(false))}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
